import { useState, useEffect, useRef } from "react";
import "./App.css";

export default function ChatWindow({ agent, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize conversation with greeting
  useEffect(() => {
    setMessages([
      {
        id: Date.now(),
        text: `Hello! I'm ${agent.name}. How can I help you today?`,
        sender: "bot",
        timestamp: new Date().toISOString(),
      },
    ]);
    // Generate a unique conversation ID
    setConversationId(`conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  }, [agent]);

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: "user",
      timestamp: new Date().toISOString(),
    };

    // Add user message immediately (optimistic UI)
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsTyping(true);
    setError(null);

    try {
      // Copilot Chat API endpoint
      const apiEndpoint = "https://qounp0lnig.execute-api.us-east-1.amazonaws.com/api/copilot-chat";

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          agent_id: agent.id,
          message: inputMessage,
          conversation_id: conversationId,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      // Add bot response
      const botMessage = {
        id: Date.now() + 1,
        text: data.response || "I received your message but couldn't generate a response.",
        sender: "bot",
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      console.error("Chat error:", err);
      setError("Failed to send message. Please try again.");
      
      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        text: "Sorry, I'm having trouble connecting right now. Please try again in a moment.",
        sender: "bot",
        timestamp: new Date().toISOString(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chat-window">
      {/* Header */}
      <div className="chat-window-header">
        <div className="chat-window-header-content">
          <span className="chat-window-icon">{agent.icon}</span>
          <div>
            <h3>{agent.name}</h3>
            <span className="chat-window-status">● {agent.status}</span>
          </div>
        </div>
        <button className="chat-window-close-btn" onClick={onClose} title="Close chat">
          ×
        </button>
      </div>

      {/* Messages */}
      <div className="chat-window-messages">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`chat-message ${msg.sender === "user" ? "chat-message-user" : "chat-message-bot"} ${
              msg.isError ? "chat-message-error" : ""
            }`}
          >
            {msg.sender === "bot" && (
              <div className="chat-message-avatar">{agent.icon}</div>
            )}
            <div className="chat-message-content">
              <div className="chat-message-text">{msg.text}</div>
              <div className="chat-message-time">
                {new Date(msg.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
            {msg.sender === "user" && (
              <div className="chat-message-avatar-user">You</div>
            )}
          </div>
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <div className="chat-message chat-message-bot">
            <div className="chat-message-avatar">{agent.icon}</div>
            <div className="chat-message-content">
              <div className="chat-typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="chat-window-error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {/* Input */}
      <div className="chat-window-input-container">
        <textarea
          className="chat-window-input"
          placeholder={`Ask ${agent.name} anything...`}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          rows="1"
          disabled={isTyping}
        />
        <button
          className="chat-window-send-btn"
          onClick={sendMessage}
          disabled={!inputMessage.trim() || isTyping}
          title="Send message"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
}
